/**
 * Adlibs and macros from the production's studio config snapshot (D22): the
 * config's adlib panels (a button per item: a piece type fired with its
 * params — now or queued — or an action run; its hotkey shown), every macro
 * action, a free adlib of any piece type with its param form, and stop per
 * source layer.
 *
 * A piece item fires with its own params in one click (on air there is no
 * time for a form); its "…" opens the param form prefilled, to change them
 * first. Which form is open is this component's UI state.
 */
import type { JsonValue } from '@acp/domain/primitives.js';
import type { ParamSpec } from '@acp/engine/studioConfig.js';
import { SlidersHorizontalIcon, SquareIcon } from 'lucide-react';
import { useState } from 'react';
import { type ActionView, type AdlibItemView, type AdlibPanelView, type ParamField, paramFields } from '../../lib/playout/adlib.js';
import type { SourceLayer } from '../../lib/playout/viewModel.js';
import { Button } from '../ui/button.js';
import { NativeSelect } from '../ui/native-select.js';
import { AdlibParamForm } from './AdlibParamForm.js';

export type PieceTypeChoice = { readonly id: string; readonly label: string; readonly params: Readonly<Record<string, ParamSpec>> };

export type AdlibRequest = { readonly pieceType: string; readonly params: Record<string, JsonValue>; readonly mode: 'now' | 'queue' };

export type AdlibPanelProps = {
  readonly panels: readonly AdlibPanelView[];
  readonly actions: readonly ActionView[];
  readonly pieceTypes: readonly PieceTypeChoice[];
  readonly layers: readonly SourceLayer[];
  /** The playlist is active and the user may take (`playout.take`). */
  readonly enabled: boolean;
  readonly onAdlib: (request: AdlibRequest) => void;
  readonly onAction: (actionId: string) => void;
  readonly onStopLayer: (layer: string) => void;
};

type OpenForm = { readonly title: string; readonly pieceType: string; readonly fields: readonly ParamField[]; readonly mode: 'now' | 'queue' };

function ItemButton({ item, enabled, onFire, onEdit }: { readonly item: AdlibItemView; readonly enabled: boolean; readonly onFire: () => void; readonly onEdit: () => void }) {
  const hotkey = item.hotkey === null ? null : <kbd className="rounded border px-1 font-mono text-xs">{item.hotkey}</kbd>;
  return (
    <div className="flex" data-testid="adlib-item">
      <Button
        variant={item.kind === 'action' ? 'secondary' : 'outline'}
        size="sm"
        className="flex-1 justify-between"
        disabled={!enabled || item.missing}
        title={item.missing ? 'Not in this production’s config snapshot' : item.kind === 'piece' ? `${item.pieceTypeLabel}${item.mode === 'queue' ? ' — queued as next' : ''}` : 'Action'}
        aria-keyshortcuts={item.hotkey ?? undefined}
        onClick={onFire}
      >
        <span className="truncate">
          {item.label}
          {item.kind === 'piece' && item.mode === 'queue' ? ' ⏭' : ''}
        </span>
        {hotkey}
      </Button>
      {item.kind === 'piece' && item.hasParams ? (
        <Button variant="ghost" size="sm" disabled={!enabled || item.missing} aria-label={`${item.label}: change params`} onClick={onEdit} data-testid="adlib-edit">
          <SlidersHorizontalIcon />
        </Button>
      ) : null}
    </div>
  );
}

export function AdlibPanel({ panels, actions, pieceTypes, layers, enabled, onAdlib, onAction, onStopLayer }: AdlibPanelProps) {
  const [form, setForm] = useState<OpenForm | null>(null);
  const [freeType, setFreeType] = useState(pieceTypes[0]?.id ?? '');
  const typeOf = (id: string) => pieceTypes.find((t) => t.id === id);
  const openForm = (title: string, pieceType: string, params: Readonly<Record<string, JsonValue>>, mode: 'now' | 'queue') => {
    setForm({ title, pieceType, fields: paramFields(typeOf(pieceType)?.params ?? {}, params), mode });
  };
  const fire = (item: AdlibItemView) => {
    if (item.kind === 'action') onAction(item.actionId);
    else onAdlib({ pieceType: item.pieceType, params: { ...item.params }, mode: item.mode });
  };
  return (
    <div className="grid gap-4" data-testid="adlib-panel">
      {panels.map((panel) => (
        <section key={panel.id} aria-label={`Adlib panel ${panel.label}`} className="grid gap-1.5">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground">{panel.label}</h3>
          {panel.items.length === 0 ? <p className="text-sm text-muted-foreground">No items.</p> : null}
          {panel.items.map((item, index) => (
            <ItemButton
              // Items have no id of their own; the panel's order is their identity.
              key={`${panel.id}:${String(index)}`}
              item={item}
              enabled={enabled}
              onFire={() => fire(item)}
              onEdit={() => (item.kind === 'piece' ? openForm(item.label, item.pieceType, item.params, item.mode) : undefined)}
            />
          ))}
        </section>
      ))}
      {actions.length > 0 ? (
        <section aria-label="Actions" className="grid gap-1.5">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Actions</h3>
          <div className="flex flex-wrap gap-1.5">
            {actions.map((a) => (
              <Button key={a.id} variant="secondary" size="sm" disabled={!enabled} title={`${String(a.steps)} step${a.steps === 1 ? '' : 's'}`} onClick={() => onAction(a.id)} data-testid="action-button">
                {a.label}
              </Button>
            ))}
          </div>
        </section>
      ) : null}
      {pieceTypes.length > 0 ? (
        <section aria-label="Adlib any piece type" className="grid gap-1.5">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Adlib</h3>
          <div className="flex gap-1.5">
            <NativeSelect aria-label="Piece type" value={freeType} onChange={(e) => setFreeType(e.target.value)} disabled={!enabled}>
              {pieceTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </NativeSelect>
            <Button variant="outline" size="sm" className="h-9" disabled={!enabled || typeOf(freeType) === undefined} onClick={() => openForm(typeOf(freeType)?.label ?? freeType, freeType, {}, 'now')} data-testid="adlib-free">
              Adlib…
            </Button>
          </div>
        </section>
      ) : null}
      {form === null ? null : (
        <AdlibParamForm
          // A fresh form (and fresh values) per opening.
          key={`${form.pieceType}:${form.title}`}
          title={form.title}
          fields={form.fields}
          mode={form.mode}
          disabled={!enabled}
          onCancel={() => setForm(null)}
          onSubmit={({ params, mode }) => {
            onAdlib({ pieceType: form.pieceType, params, mode });
            setForm(null);
          }}
        />
      )}
      {layers.length > 0 ? (
        <section aria-label="Stop a layer" className="grid gap-1.5">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Stop</h3>
          <div className="flex flex-wrap gap-1.5">
            {layers.map((l) => (
              <Button key={l.id} variant="ghost" size="sm" disabled={!enabled} onClick={() => onStopLayer(l.id)} data-testid="stop-layer" aria-label={`Stop ${l.label}`}>
                <SquareIcon />
                {l.label}
              </Button>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
